"use client"
import { type FragmentOf, readFragment } from "@opensea/graphql"
import {
  Flex,
  FlexColumn,
  Modal,
  ModalBody,
  ModalHeader,
  ModalHeaderTitle,
  RadioGroup,
  RadioGroupItem,
  TextBody,
  ToggleButtonGroup,
  ToggleButtonGroupItem,
} from "@opensea/ui-kit"
import { differenceInMilliseconds, isBefore, parseISO } from "date-fns"
import { useTranslations } from "next-intl"
import { useEffect, useState } from "react"
import { useShallow } from "zustand/react/shallow"
import { useDropStore } from "@/components/collection/drops/components/CollectionDropContext"
import { useFormatStageDate } from "@/components/collection/drops/use-format-stage-date"
import { AddtoCalendarOptions } from "./AddtoCalendarOptions"
import { EmailAddressOption } from "./EmailAddressOption"
import { SetDropReminderModalFragment } from "./SetDropReminderModal.fragment"

type SetDropReminderModalProps = {
  drop: FragmentOf<typeof SetDropReminderModalFragment> | null
}

type ReminderType = "calendar" | "email"

export function SetDropReminderModal({
  drop: dropKey,
}: SetDropReminderModalProps) {
  const drop = readFragment(SetDropReminderModalFragment, dropKey)
  const [
    collectionSlug,
    isReminderModalOpen,
    setIsReminderModalOpen,
    activeStageIndex,
  ] = useDropStore(
    useShallow(state => [
      state.collectionSlug,
      state.isReminderModalOpen,
      state.setIsReminderModalOpen,
      state.activeStageIndex,
    ]),
  )
  const formatStageDate = useFormatStageDate()
  const activeStage = drop?.stages.find(
    stage => stage.stageIndex === activeStageIndex,
  )
  const t = useTranslations("SetDropReminderModal")

  const ONE_HOUR_MS = 60 * 60 * 1000
  const stageDates =
    drop?.stages
      ?.map(s => parseISO(s.startTime))
      .filter(d => Number.isFinite(d.getTime())) ?? []
  const firstStageDate =
    stageDates.length > 0
      ? new Date(Math.min(...stageDates.map(d => d.getTime())))
      : undefined
  const now = new Date()
  const isBeforeFirstStage =
    firstStageDate !== undefined && isBefore(now, firstStageDate)
  const withinOneHourOfFirstStage =
    isBeforeFirstStage &&
    firstStageDate !== undefined &&
    differenceInMilliseconds(firstStageDate, now) <= ONE_HOUR_MS

  const [reminderType, setReminderType] = useState<ReminderType>(
    withinOneHourOfFirstStage ? "calendar" : "email",
  )

  useEffect(() => {
    if (withinOneHourOfFirstStage && reminderType === "email") {
      setReminderType("calendar")
    }
  }, [withinOneHourOfFirstStage, reminderType])

  const [selectedStageIndex, setSelectedStageIndex] = useState(
    String(activeStage?.stageIndex ?? 0),
  )
  const selectedStage = drop?.stages.find(
    stage => stage.stageIndex === Number(selectedStageIndex),
  )

  if (!drop) {
    return null
  }

  return (
    <Modal
      content={
        <>
          <ModalHeader>
            <ModalHeaderTitle>{t("title")}</ModalHeaderTitle>
          </ModalHeader>
          <ModalBody>
            <RadioGroup
              onValueChange={val => {
                if (val != null) {
                  setSelectedStageIndex(val)
                }
              }}
              value={String(selectedStageIndex)}
            >
              {drop.stages.map(stage => {
                return (
                  <Flex className="items-center gap-3" key={stage.stageIndex}>
                    <RadioGroupItem value={String(stage.stageIndex)} />
                    <FlexColumn>
                      <TextBody asChild size="sm" weight="semibold">
                        <label>{stage.label || "Public stage"}</label>
                      </TextBody>
                      <TextBody
                        className="font-mono text-text-tertiary"
                        size="xs"
                      >
                        {formatStageDate(stage.startTime)}
                      </TextBody>
                    </FlexColumn>
                  </Flex>
                )
              })}
            </RadioGroup>
            {withinOneHourOfFirstStage ? (
              <TextBody className="mt-6 mb-2" size="md" weight="semibold">
                {t("chooseCalendar")}
              </TextBody>
            ) : (
              <ToggleButtonGroup
                className="mt-6"
                onValueChange={(type: ReminderType) => {
                  setReminderType(type)
                }}
                value={reminderType}
              >
                <ToggleButtonGroupItem
                  className="w-full justify-center"
                  value="email"
                >
                  {t("emailReminders")}
                </ToggleButtonGroupItem>

                <ToggleButtonGroupItem
                  className="w-full justify-center"
                  value="calendar"
                >
                  {t("addToCalendar")}
                </ToggleButtonGroupItem>
              </ToggleButtonGroup>
            )}
            <FlexColumn className="mt-4 gap-4">
              {withinOneHourOfFirstStage || reminderType === "calendar" ? (
                <AddtoCalendarOptions
                  drop={drop}
                  selectedStageIndex={selectedStageIndex}
                />
              ) : (
                <EmailAddressOption
                  collectionSlug={collectionSlug}
                  selectedStageUuid={selectedStage?.uuid}
                />
              )}
            </FlexColumn>
          </ModalBody>
        </>
      }
      onOpenChange={isOpen => setIsReminderModalOpen(isOpen)}
      open={isReminderModalOpen}
    />
  )
}
